/**
 * learningResources.js
 * --------------------------------------------------------------------------
 * Hand-curated learning recommendations per skill.
 *
 * Real platforms, realistic course/programme names. NOT AI-generated and
 * NOT linked, links would risk hallucinated URLs or stale content. The
 * worker uses the title + provider to find the current best version on
 * the provider's site themselves.
 *
 * Recommendations are biased toward EU-relevant providers (INSEAD, IESE,
 * IMD, ESMT) alongside global options (LinkedIn Learning, Coursera, edX).
 *
 * Each skill has 2-4 recommendations spanning short courses (4-8 hours,
 * free or low-cost) and longer programmes (multi-week, paid). Worker can
 * pick the depth appropriate to their goal.
 *
 * Reviewer: Pascal Schumachers, last reviewed 2026-01-15.
 */

export const RESOURCE_TYPE = {
  course:    'course',
  cert:      'cert',
  book:      'book',
  programme: 'programme',
};

const r = (provider, title, type, hours) => ({ provider, title, type, hours });

export const LEARNING_RESOURCES = {
  // ─── Sales / Enterprise ──────────────────────────────────────────────
  enterpriseSales: [
    r('LinkedIn Learning', 'Strategic Selling',                        'course',    6),
    r('Coursera (Northwestern)', 'The Art of Sales: Mastering the Selling Process', 'programme', 50),
    r('Sandler Training', 'Enterprise Selling System',                 'cert',      40),
  ],
  accountManagement: [
    r('LinkedIn Learning', 'Strategic Account Management',             'course',    5),
    r('Miller Heiman Group', 'Strategic Selling with Perspective',     'programme', 24),
    r('Coursera (Yale)', 'Connected Strategy',                         'course',    12),
  ],
  pipelineManagement: [
    r('LinkedIn Learning', 'Sales Pipeline Management',                'course',    4),
    r('Pavilion', 'Sales Operations School',                           'programme', 30),
    r('Salesforce Trailhead', 'Sales Cloud Pipeline Mastery',          'cert',      20),
  ],
  negotiation: [
    r('Coursera (Yale)', 'Introduction to Negotiation',                'course',    18),
    r('Harvard PON', 'Negotiation and Leadership',                     'programme', 24),
    r('Book', 'Getting to Yes, Roger Fisher, William Ury',            'book',      6),
  ],
  crm: [
    r('Salesforce Trailhead', 'Salesforce Administrator Certification','cert',      80),
    r('LinkedIn Learning', 'CRM Strategy Fundamentals',                'course',    3),
    r('HubSpot Academy', 'CRM for Revenue Teams',                      'course',    5),
  ],
  csuite: [
    r('IESE Business School', 'Selling to the C-Suite',                'programme', 16),
    r('LinkedIn Learning', 'Communicating with Confidence to Executives', 'course', 4),
    r('Book', 'Selling to the C-Suite, Nicholas Read, Stephen Bistritz', 'book',   8),
    r('Harvard Business Review', 'How to Engage with the C-Suite (HBR Guide)', 'book', 5),
  ],
  saas: [
    r('Pavilion', 'CRO School (SaaS GTM)',                             'programme', 40),
    r('Coursera (Wharton)', 'Customer Analytics',                      'course',    15),
    r('Book', 'From Impossible to Inevitable, Aaron Ross, Jason Lemkin', 'book',  10),
  ],
  pAndL: [
    r('Coursera (Wharton)', 'Introduction to Financial Accounting',    'course',    24),
    r('INSEAD Online', 'P&L Management for Senior Leaders',            'programme', 12),
    r('Book', 'Financial Intelligence, Karen Berman, Joe Knight',     'book',      10),
  ],
  forecastAccuracy: [
    r('LinkedIn Learning', 'Sales Forecasting',                        'course',    3),
    r('Coursera (Wharton)', 'Business Analytics Specialization',       'programme', 60),
    r('Book', 'Predictable Revenue, Aaron Ross',                      'book',      6),
  ],
  teamLeadership: [
    r('Coursera (Michigan)', 'Leading People and Teams',               'programme', 40),
    r('INSEAD Online', 'Leading High-Performing Teams',                'programme', 16),
    r('LinkedIn Learning', 'Coaching and Developing Employees',        'course',    4),
  ],
  goToMarket: [
    r('Pavilion', 'Go-to-Market School',                               'programme', 30),
    r('Reforge', 'GTM Strategy & Operations',                          'programme', 20),
    r('Book', 'Crossing the Chasm, Geoffrey Moore',                   'book',      8),
  ],
  strategicPlanning: [
    r('Coursera (UVA Darden)', 'Strategic Planning and Execution',     'course',    12),
    r('IMD', 'Strategic Thinking',                                     'programme', 32),
    r('Book', 'Good Strategy / Bad Strategy, Richard Rumelt',         'book',      9),
  ],
  enablement: [
    r('Sales Enablement Society', 'Enablement Practitioner Certification', 'cert',  20),
    r('LinkedIn Learning', 'Sales Enablement Foundations',             'course',    4),
    r('Pavilion', 'Sales Enablement Manager School',                   'programme', 24),
  ],
  channelPartner: [
    r('LinkedIn Learning', 'Channel Sales Strategy',                   'course',    3),
    r('Channel Mechanics', 'Partner Programme Design',                 'programme', 16),
    r('Book', 'The Channel Advantage, Lawrence Friedman, Tim Furey',  'book',      7),
  ],
  territoryPlanning: [
    r('LinkedIn Learning', 'Territory and Quota Planning',             'course',    3),
    r('Pavilion', 'Sales Operations School',                           'programme', 30),
  ],
  demandGen: [
    r('Coursera (Northwestern)', 'Content, Advertising & Social IMC',  'course',    18),
    r('CXL', 'Demand Generation Mastery',                              'programme', 24),
  ],

  // ─── HR / People ──────────────────────────────────────────────────────
  compStrategy: [
    r('WorldatWork', 'Certified Compensation Professional (CCP)',      'cert',      120),
    r('LinkedIn Learning', 'Compensation Strategy',                    'course',    4),
    r('Book', 'The Compensation Handbook, Lance Berger',              'book',      14),
  ],
  payEquity: [
    r('SHRM', 'Pay Equity Strategy and Practice',                      'course',    8),
    r('IDS Pay', 'EU Pay Transparency Directive Practitioner',         'programme', 16),
    r('LinkedIn Learning', 'Pay Equity Analysis',                      'course',    3),
  ],
  talentMgmt: [
    r('Coursera (Michigan)', 'Human Resource Management',              'programme', 48),
    r('CIPD', 'Level 5 Associate Diploma in People Management',        'cert',      120),
    r('LinkedIn Learning', 'Talent Management',                        'course',    4),
  ],
  orgDesign: [
    r('LinkedIn Learning', 'Organisation Design',                      'course',    4),
    r('INSEAD Online', 'Organisation Design Essentials',               'programme', 24),
    r('Book', 'Designing Your Organisation, Amy Kates, Jay Galbraith', 'book',     12),
  ],
  employeeRelations: [
    r('CIPD', 'Employee Relations',                                    'course',    16),
    r('LinkedIn Learning', 'Handling Employee Relations Cases',        'course',    3),
  ],
  hrAnalytics: [
    r('Coursera (Wharton)', 'People Analytics',                        'course',    16),
    r('AIHR', 'People Analytics Certificate',                          'cert',      36),
    r('Book', 'The Power of People, Nigel Guenole et al.',            'book',      10),
  ],
  workdayHris: [
    r('Workday Learning', 'Workday HCM Fundamentals',                  'course',    16),
    r('SAP', 'SuccessFactors Employee Central Foundation',             'cert',      30),
  ],
  gdprHr: [
    r('IAPP', 'CIPP/E European Data Protection Certification',        'cert',      40),
    r('LinkedIn Learning', 'GDPR Compliance Essentials',               'course',    3),
  ],
  change: [
    r('Prosci', 'Change Management Practitioner Certification',        'cert',      24),
    r('LinkedIn Learning', 'Leading Change',                           'course',    3),
    r('Book', 'Leading Change, John Kotter',                          'book',      9),
  ],

  // ─── Product / general business ──────────────────────────────────────
  productMgmt: [
    r('Reforge', 'Product Strategy',                                   'programme', 24),
    r('Product School', 'Product Manager Certification (PMC)',         'cert',      40),
    r('Book', 'Inspired, Marty Cagan',                                'book',      8),
  ],
  roadmap: [
    r('LinkedIn Learning', 'Product Roadmaps',                         'course',    3),
    r('ProductPlan', 'Roadmap Masterclass',                            'course',    6),
  ],
  userResearch: [
    r('Coursera (Michigan)', 'UX Research at Scale',                   'course',    16),
    r('NN/g', 'User Research Methods',                                 'cert',      24),
  ],
  analytics: [
    r('Coursera (Google)', 'Google Data Analytics Professional Certificate', 'programme', 180),
    r('Coursera (Wharton)', 'Business Analytics Specialization',       'programme', 60),
    r('LinkedIn Learning', 'Data Fluency: Exploring and Describing Data', 'course', 3),
  ],
  stakeholder: [
    r('LinkedIn Learning', 'Stakeholder Management',                   'course',    3),
    r('PMI', 'Project Management Stakeholder Engagement (PMP module)', 'course',    8),
    r('Book', 'Influence Without Authority, Allan Cohen, David Bradford', 'book',  8),
  ],
  projectMgmt: [
    r('PMI', 'Project Management Professional (PMP)',                  'cert',      120),
    r('AXELOS', 'PRINCE2 Practitioner',                                'cert',      80),
    r('Coursera (Google)', 'Google Project Management Certificate',    'programme', 100),
  ],
  communication: [
    r('Coursera (Wharton)', 'Improving Communication Skills',          'course',    9),
    r('LinkedIn Learning', 'Executive Presence on Video Conferences',  'course',    2),
    r('Book', 'Talk Like TED, Carmine Gallo',                         'book',      7),
  ],
  mentoring: [
    r('LinkedIn Learning', 'Coaching Skills for Leaders and Managers', 'course',    4),
    r('Coursera (UC Davis)', 'Coaching Practices',                     'course',    18),
  ],

  // ─── Engineering ─────────────────────────────────────────────────────
  systemDesign: [
    r('Educative', 'Grokking the System Design Interview',             'course',    25),
    r('Book', 'Designing Data-Intensive Applications, Martin Kleppmann', 'book',  20),
    r('ByteByteGo', 'System Design Interview Volume 1',                'book',     12),
  ],
  cicd: [
    r('LinkedIn Learning', 'DevOps Foundations: CI/CD',                'course',    3),
    r('Coursera (Google)', 'Continuous Integration on Google Cloud',   'course',    8),
  ],
  k8s: [
    r('CNCF', 'Certified Kubernetes Administrator (CKA)',              'cert',      40),
    r('Linux Foundation', 'Introduction to Kubernetes',                'course',    14),
  ],
  docker: [
    r('Docker', 'Docker Mastery',                                      'course',    20),
    r('Coursera (IBM)', 'Introduction to Containers, Docker, Kubernetes', 'course', 12),
  ],
  aws: [
    r('AWS Training', 'AWS Solutions Architect Associate',             'cert',      80),
    r('A Cloud Guru', 'AWS Certified Developer - Associate',           'cert',      60),
  ],
  agile: [
    r('Scrum.org', 'Professional Scrum Master I (PSM I)',              'cert',      16),
    r('LinkedIn Learning', 'Agile Foundations',                        'course',    2),
  ],
  codeReview: [
    r('LinkedIn Learning', 'Code Reviews, Best Practices',            'course',    2),
    r('Book', 'Software Engineering at Google (Code Review chapter)',  'book',      4),
  ],
};

/**
 * Get learning resources for a skill. Returns [] if the skill has no
 * curated resources yet (which is fine, the UI shows "No recommendations
 * yet" in that case rather than fabricating links).
 */
export function getResourcesFor(skillId) {
  return LEARNING_RESOURCES[skillId] || [];
}
